import { Component, OnInit, OnDestroy, ViewEncapsulation, Input } from '@angular/core';
import { lensEventRoutes } from 'common/common/constants/lens';
import { KeyboardKeyCodes } from 'common/common/constants/keyboard-key-codes';
import { bcSessionKeys } from 'common/common/constants/session-keys';
import { WorkItemListService } from 'common/workitems/workitem-list.service';
import { RxEmitter } from 'common/rxjs/rx-emitter';
import { WorkItemLens } from 'common/workitems/models/workitem-lens';
import { WorkItemAdapter } from 'common/workitems/workitem-adapter';
import { CoolLocalStorage } from '@angular-cool/storage';
import { Subscription } from 'rxjs';
import { merge } from 'lodash';
import { taskItemAnimation } from '../shared/animations/task-item.animation';

@Component({
  selector: 'app-work-item-lens-list',
  templateUrl: './work-item-lens-list.component.html',
  styleUrls: ['./work-item-lens-list.component.scss'],
  animations: [taskItemAnimation],
  encapsulation: ViewEncapsulation.None
})
export class WorkItemLensListComponent implements OnInit, OnDestroy {
  @Input()
  isHybrid: boolean;
  public lensWorkItemList: WorkItemLens[];

  private _sessionEndedSubscription: Subscription;
  private _navigationChangedSubscription: Subscription;
  private _visitorInfoChangedSubscription: Subscription;

  constructor(private _workItemListService: WorkItemListService,
              private _rxEmitter: RxEmitter,
              private _localStorageService: CoolLocalStorage) {

    this.lensWorkItemList = this._workItemListService.getLensWorkItemList();
  }

  ngOnInit(): void {
    this._sessionEndedSubscription = this._rxEmitter.subscribe(lensEventRoutes.sessionEnded, this._removeLensSession.bind(this));
    this._navigationChangedSubscription = this._rxEmitter.subscribe(lensEventRoutes.navigationChanged, this._updateLensRoute.bind(this));
    this._visitorInfoChangedSubscription = this._rxEmitter.subscribe(lensEventRoutes.visitorInfoChanged, this._updateVisitorInfo.bind(this));

    this._loadFromStorage();
  }

  ngOnDestroy(): void {
    if (this._sessionEndedSubscription) {
      this._sessionEndedSubscription.unsubscribe();
      this._sessionEndedSubscription = null;
    }
    if (this._navigationChangedSubscription) {
      this._navigationChangedSubscription.unsubscribe();
      this._navigationChangedSubscription = null;
    }
    if (this._visitorInfoChangedSubscription) {
      this._visitorInfoChangedSubscription.unsubscribe();
      this._visitorInfoChangedSubscription = null;
    }
  }

  public onNewLensClicked(): void {
    const workItem = WorkItemAdapter.createLensItem();

    this._addWorkItemToList(workItem);
    this.selectWorkItem(workItem);
  }

  public selectWorkItem(workItem): void {
    this._workItemListService.selectWorkitem(workItem);
    this._localStorageService.setItem(bcSessionKeys.storage.lensWorkItem, JSON.stringify(this.lensWorkItemList));
  }

  public onWorkItemKeydown(workItem, event): void {
    if (event.which !== KeyboardKeyCodes.ENTER) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    this.selectWorkItem(workItem);
  }

  public generateWorkItemClass(workItem): string {
    const generatedTheme = this._workItemListService.generateWorkitemClass(workItem);

    // We need to reset it to always blue
    return generatedTheme.replace(/theme-\d+/, 'theme-7');
  }

  public get lensSessionsCount(): number {
    return this.lensWorkItemList.length;
  }

  public isLensButtonDisabled(): boolean {
    return this._workItemListService.isLiveSessionActive();
  }

  private _loadFromStorage(): void {
    const savedLensWorkItem = this._localStorageService.getItem(bcSessionKeys.storage.lensWorkItem);
    let loadedLensWorkItem = null;
    if (savedLensWorkItem) {
      loadedLensWorkItem = JSON.parse(savedLensWorkItem)[0];
    }

    if (loadedLensWorkItem) {
      const lensWorkItem = WorkItemAdapter.createLensItem(loadedLensWorkItem);
      this._addWorkItemToList(lensWorkItem);
    }
  }

  private _saveToStorage(): void {
    this._localStorageService.setItem(bcSessionKeys.storage.lensWorkItem, JSON.stringify(this.lensWorkItemList));
  }

  private _updateLensRoute(request): void {
    this.lensWorkItemList[0].elmRoute = request.newUrl;
    this._saveToStorage();
  }

  private _addWorkItemToList(lensWorkItem): void {
    if (this.lensSessionsCount > 0) {
      return;
    }

    this.lensWorkItemList.push(lensWorkItem);
    this._saveToStorage();
  }

  private _updateVisitorInfo(changes): void {
    merge(this.lensWorkItemList[0], changes.updatedFields);
  }

  private _removeLensSession(): void {
    if (this.lensSessionsCount > 0) {
      const workItem = this.lensWorkItemList.pop();
      this._workItemListService.removeWorkItem(workItem);
      this._saveToStorage();
    }
  }
}
